import Card from '@packages/ui/card/card.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import CardTableContent from '@packages/ui/card/card-content-table.tsx';
import { useTableReservations } from '@client/page/management/table-reservation/list/logic/use-table-reservations.ts';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import { useTranslate } from '@tolgee/react';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import HistorySearchBar from '@client/page/management/appointment/history/component/history-search-bar.tsx';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { TableReservationItemFragment } from '@client/graphql/types/graphql.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TableReservationHistoryTableList from './component/reservation-history-table-list';
import TableReservationListStats from './component/table-reservation-list-stats';

const TableReservationHistory = (props: {
  initialStartTime: Dayjs;
  initialEndTime: Dayjs;
  filter: (data: { startTime: Dayjs; endTime: Dayjs; onChange: (dates: [Dayjs, Dayjs]) => void }) => ReactNode;
  footer?: (data: { startTime: Dayjs; endTime: Dayjs; onChange: (dates: [Dayjs, Dayjs]) => void }) => ReactNode;
}) => {
  const [searchQuery] = useSearchParams();
  const branchProvider = useFocusedBranchContext();
  const branchID = branchProvider.branch?.id ?? '';
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [startTime, setStartTime] = useState<Dayjs>(() => {
    if (searchQuery.get('nearestDetailTime')) {
      return dayjs(searchQuery.get('nearestDetailTime')).startOf('month');
    }
    return props.initialStartTime;
  });
  const [endTime, setEndTime] = useState<Dayjs>(() => {
    if (searchQuery.get('nearestDetailTime')) {
      return dayjs(searchQuery.get('nearestDetailTime')).endOf('month');
    }
    return props.initialEndTime;
  });

  const { data, loading, error, refetch } = useTableReservations(branchID, {
    startTime: startTime.format('YYYY-MM-DDTHH:mm'),
    endTime: endTime.format('YYYY-MM-DDTHH:mm'),
  });

  const [reservations, setReservations] = useState<TableReservationItemFragment[]>([]);

  useEffect(() => {
    setReservations(data.filter((item) => !item.deleted));
  }, [data, setReservations]);

  const handleQuickPick = (type: 'currentMonth' | 'previousMonth' | 'nextMonth') => {
    let newStartTime, newEndTime;
    const today = dayjs();

    switch (type) {
      case 'currentMonth':
        newStartTime = today.startOf('month');
        newEndTime = today.endOf('month');
        break;
      case 'previousMonth':
        newStartTime = startTime.subtract(1, 'month').startOf('month');
        newEndTime = startTime.subtract(1, 'month').endOf('month');
        break;
      case 'nextMonth':
        newStartTime = startTime.add(1, 'month').startOf('month');
        newEndTime = startTime.add(1, 'month').endOf('month');
        break;
    }

    setStartTime(newStartTime);
    setEndTime(newEndTime);
  };

  const handleSearchBar = useCallback(
    (searchString: string) => {
      setReservations(
        data.filter((reservation) => {
          if (reservation.guestName.toLowerCase().includes(searchString.toLowerCase())) {
            return true;
          }
          if (reservation.phone.toLowerCase().includes(searchString.toLowerCase())) {
            return true;
          }
          if (reservation.email.toLowerCase().includes(searchString.toLowerCase())) {
            return true;
          }
          return false;
        })
      );
    },
    [data, setReservations]
  );

  return (
    <Card>
      <CardHeader title={t('table-reservation.history.header', 'History')} withBackButton={true} backTarget={-1}>
        <div className="flex gap-4">
          <SecondaryButton
            onClick={() => {
              handleQuickPick('previousMonth');
            }}
            className="flex items-center"
          >
            <ChevronLeftIcon className="hidden sm:block w-4 h-4" />
            {t('common.button.previous-month', 'Previous Month')}
          </SecondaryButton>
          <SecondaryButton
            onClick={() => {
              handleQuickPick('currentMonth');
            }}
          >
            {t('common.button.current-month')}
          </SecondaryButton>
          <SecondaryButton
            onClick={() => {
              handleQuickPick('nextMonth');
            }}
            className="flex items-center"
          >
            {t('common.button.next-month', 'Next Month')}
            <ChevronRightIcon className="hidden sm:block w-4 h-4" />
          </SecondaryButton>
        </div>
        {props.filter({
          startTime: startTime,
          endTime: endTime,
          onChange: (dates) => {
            setStartTime(dates[0]);
            setEndTime(dates[1]);
          },
        })}
      </CardHeader>

      <CardTableContent>
        <HistorySearchBar onChange={handleSearchBar} />
        <TableReservationListStats loading={loading} items={reservations} />
        <TableReservationHistoryTableList
          onClick={(id) => {
            navigate(id);
          }}
          error={error?.message}
          refetch={() => {
            void refetch();
          }}
          loading={loading}
          items={reservations}
        />
      </CardTableContent>

      {props.footer && (
        <CardFooter>
          {props.footer({
            startTime: startTime,
            endTime: endTime,
            onChange: (dates) => {
              setStartTime(dates[0]);
              setEndTime(dates[1]);
            },
          })}
        </CardFooter>
      )}
    </Card>
  );
};

export default TableReservationHistory;
